import { useCallback, useEffect, useRef, useState } from 'react';

const hasNextPage = (page, pagination) =>
    pagination ? page < pagination.pageCount : false;

/**
 * The paging engine behind every "Load more" list.
 *
 * Callers supply `fetchPage(page)`, which resolves to
 * `{ items, pagination }`. How those rows are fetched — REST, GraphQL,
 * whatever — is none of this hook's business.
 *
 * A list is seeded one of two ways:
 *  - `initialItems` / `initialPagination` from a router loader, and it
 *    resyncs whenever the loader hands over a fresh page 1, or
 *  - `resetKey`, in which case the hook fetches page 1 itself and refetches
 *    whenever that key changes.
 */
export const usePaginatedList = ({
    fetchPage,
    initialItems,
    initialPagination,
    resetKey,
}) => {
    const selfFetching = resetKey !== undefined;

    const [items, setItems] = useState(() => initialItems ?? []);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    // A CMS without pagination support returns no meta.pagination. Treating
    // that as "no more pages" is what keeps the Load more button hidden.
    const [hasMore, setHasMore] = useState(() =>
        hasNextPage(1, initialPagination),
    );

    // Bumped whenever the list restarts from page 1. Any in-flight request
    // whose token no longer matches is stale and is discarded.
    const requestIdRef = useRef(0);

    // `loading` is captured per render, so two clicks in the same tick both
    // see it as false. A ref is read live and blocks the second call.
    const inFlightRef = useRef(false);

    const fetchPageRef = useRef(fetchPage);
    fetchPageRef.current = fetchPage;

    // Seeded lists: resync when the loader hands over fresh data.
    const initialItemsRef = useRef(initialItems);
    useEffect(() => {
        if (selfFetching) return;
        if (initialItemsRef.current === initialItems) return;
        initialItemsRef.current = initialItems;
        requestIdRef.current += 1;
        setItems(initialItems ?? []);
        setPage(1);
        setHasMore(hasNextPage(1, initialPagination));
    }, [selfFetching, initialItems, initialPagination]);

    // Self-fetching lists: load page 1, and reload it when the key changes.
    useEffect(() => {
        if (!selfFetching || !resetKey) return undefined;

        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        let isActive = true;
        inFlightRef.current = true;
        setLoading(true);

        (async () => {
            try {
                const result = await fetchPageRef.current(1);
                if (!isActive || requestId !== requestIdRef.current) return;
                setItems(result?.items ?? []);
                setPage(1);
                setHasMore(hasNextPage(1, result?.pagination));
            } catch (error) {
                console.log({ loadFirstPageError: error });
            } finally {
                if (isActive && requestId === requestIdRef.current) {
                    inFlightRef.current = false;
                    setLoading(false);
                }
            }
        })();

        return () => {
            isActive = false;
        };
    }, [selfFetching, resetKey]);

    const loadMore = useCallback(async () => {
        if (inFlightRef.current || loading || !hasMore) return;

        const requestId = requestIdRef.current;
        const nextPage = page + 1;
        inFlightRef.current = true;
        setLoading(true);

        try {
            const result = await fetchPageRef.current(nextPage);
            // The list restarted while this page was in flight: these rows
            // belong to the old query, so drop them.
            if (requestId !== requestIdRef.current) return;
            setItems((previous) => [...previous, ...(result?.items ?? [])]);
            setPage(nextPage);
            setHasMore(hasNextPage(nextPage, result?.pagination));
        } catch (error) {
            console.log({ loadMoreError: error });
        } finally {
            if (requestId === requestIdRef.current) {
                inFlightRef.current = false;
                setLoading(false);
            }
        }
    }, [loading, hasMore, page]);

    return { items, loadMore, loading, hasMore };
};
